import React, { useMemo } from 'react';
import { usePIM } from '../context/PIMContext';

const ProjectProgressBar = () => {
    const { activeCharge, components, productData, now } = usePIM(); // Use components instead of separate accessories list

    // Helper: Determine Status Color
    const getStatusColor = (status) => {
        if (!status) return 'bg-blue-500'; // Default Blue (High Visibility Fallback)
        const s = status.toLowerCase();
        if (s.includes('production')) return 'bg-yellow-500';
        if (s.includes('development')) return 'bg-blue-500';
        if (s.includes('produced')) return 'bg-green-500';
        if (s.includes('draft')) return 'bg-stone-500';
        if (s.includes('archived')) return 'bg-red-500';
        return 'bg-blue-500'; // Fallback
    };

    // Helper: Determine Tooltip Style (Ghost Design)
    const getTooltipStyles = (status) => {
        if (!status) return 'bg-stone-900/95 border-stone-700 text-stone-200';
        const s = status.toLowerCase();

        // Return classes for: Background (semi-transparent), Border (subtle), Text (readable)

        // Production: Yellow/Amber
        if (s.includes('production')) return 'bg-yellow-950/90 border-yellow-500/50 text-yellow-100 shadow-[0_4px_20px_rgba(234,179,8,0.3)]';

        // Development: Blue
        if (s.includes('development')) return 'bg-blue-950/90 border-blue-500/50 text-blue-100 shadow-[0_4px_20px_rgba(37,99,235,0.3)]';

        // Produced: Green (Completion)
        if (s.includes('produced')) return 'bg-green-950/90 border-green-500/50 text-green-100 shadow-[0_4px_20px_rgba(22,163,74,0.3)]';

        // Draft: Stone
        if (s.includes('draft')) return 'bg-stone-900/90 border-stone-600/50 text-stone-200';

        // Archived: Red
        if (s.includes('archived')) return 'bg-red-950/90 border-red-500/50 text-red-100 shadow-[0_4px_20px_rgba(220,38,38,0.3)]';

        return 'bg-stone-900/95 border-stone-700 text-stone-200';
    };

    // --- Upper Bar: Time Progress ---
    const timeProgressData = useMemo(() => {
        if (!activeCharge) return null;

        // Use the stable 'now' from state
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0); // Normalize to start of day for comparison

        // 1. Extract potential dates
        const potentialMilestones = [
            { key: 'created_at', label: 'Created', date: activeCharge.created_at }, // Start Date
            { key: 'production_date', label: 'Production', date: activeCharge.production_date },
            { key: 'inspection_date', label: 'Inspection', date: activeCharge.inspection_date },
            { key: 'shipping_date', label: 'Shipping', date: activeCharge.shipping_date },
            { key: 'delivery_date', label: 'Delivery', date: activeCharge.delivery_date },
            { key: 'today', label: 'Today', date: todayDate.toISOString(), isToday: true }
        ];

        // 2. Filter valid dates and sort them
        const validMilestones = potentialMilestones
            .filter(m => m.date)
            .map(m => ({ ...m, timestamp: new Date(m.date).getTime() }))
            .sort((a, b) => a.timestamp - b.timestamp);

        if (validMilestones.length < 2) return null;

        const start = validMilestones[0];
        const end = validMilestones[validMilestones.length - 1];
        const totalDuration = end.timestamp - start.timestamp;

        // 3. Calculate overall progress percentage
        let progressPercent = 0;
        if (totalDuration > 0) {
            const todayMilestone = validMilestones.find(m => m.isToday);
            if (todayMilestone) {
                progressPercent = ((todayMilestone.timestamp - start.timestamp) / totalDuration) * 100;
            } else {
                progressPercent = ((now - start.timestamp) / totalDuration) * 100;
            }
        }

        progressPercent = Math.max(0, Math.min(100, progressPercent));

        // 4. Calculate positions
        const milestones = validMilestones.map(m => ({
            ...m,
            position: totalDuration === 0 ? (m.isToday ? 100 : 0) : ((m.timestamp - start.timestamp) / totalDuration) * 100
        }));

        // Determine main bar color based on ACTIVE CHARGE status or fallback to Product Status
        const activeStatus = (activeCharge && activeCharge.status) ? activeCharge.status : productData.status;
        const mainColorClass = getStatusColor(activeStatus);
        const tooltipStyleClass = getTooltipStyles(activeStatus);

        return { progressPercent, milestones, mainColorClass, tooltipStyleClass };
    }, [activeCharge, now, productData.status]);

    // --- Lower Bar: Package Content Status ---
    // Filter components: Must be 'accessory', must be 'package_content', and match active charge (or be global)
    const packageContent = components.filter(c =>
        c.role === 'accessory' &&
        c.is_package_content &&
        (!c.charge_id || (activeCharge && c.charge_id === activeCharge.id))
    );

    const contentProgressData = packageContent.length > 0 ? packageContent.map(acc => {
        // Prepare dates if available (from backend JOIN)
        const dates = [];
        if (acc.acc_created_at) dates.push({ label: 'Created', date: acc.acc_created_at });
        if (acc.acc_production_date) dates.push({ label: 'Production', date: acc.acc_production_date });
        if (acc.acc_inspection_date) dates.push({ label: 'Inspection', date: acc.acc_inspection_date });
        if (acc.acc_shipping_date) dates.push({ label: 'Shipping', date: acc.acc_shipping_date });
        if (acc.acc_delivery_date) dates.push({ label: 'Delivery', date: acc.acc_delivery_date });

        // Determine effective color
        let colorVal = undefined;
        let colorCls = undefined;

        if (acc.status_color) {
            colorVal = acc.status_color;
        } else {
            colorCls = getStatusColor(acc.status);
        }

        // Force override for standard statuses to ensure YELLOW for Production
        const sLower = (acc.status || '').toLowerCase();
        if (sLower.includes('production')) {
            colorVal = undefined;
            colorCls = 'bg-yellow-500';
        }

        return {
            id: acc.id, // Component ID
            name: acc.model_name,
            sku: acc.sku,
            status: acc.status || 'Unknown',
            color: colorVal,
            colorClass: colorCls,
            tooltipClass: getTooltipStyles(acc.status),
            eta: acc.eta || 'TBD',
            width: 100 / packageContent.length,
            dates: dates
        };
    }) : null;

    if (!activeCharge && !productData.sku) return null;

    return (
        <div className="w-full px-4 py-1 flex flex-col gap-0 select-none mt-1 relative">

            {/* Upper Bar: Time */}
            {/* Reduced Top Margin (mt-2 -> mt-1) and Bottom Margin (mb-4 -> mb-1.5) for ~6px gap */}
            <div className="h-[6px] w-full bg-stone-800 rounded-full relative group/time flex items-center z-[40] mt-1 mb-1.5">
                {/* INVISIBLE HIT AREA EXPANDER: Makes hovering easier (-top-4 -bottom-4) */}
                <div className="absolute -top-4 -bottom-4 left-0 right-0 z-[50]" />

                {timeProgressData ? (
                    <>
                        {/* Background Track */}
                        <div className="absolute inset-0 bg-stone-800 rounded-full pointer-events-none"></div>

                        {/* Progress Fill - Visual Only */}
                        {/* Added opacity-40 and hover:opacity-100 logic (Instant feedback on bar brightness) */}
                        <div
                            className={`absolute left-0 top-0 bottom-0 rounded-full transition-opacity duration-300 pointer-events-none ${timeProgressData.mainColorClass} opacity-40 group-hover/time:opacity-100`}
                            style={{ width: `${timeProgressData.progressPercent}%` }}
                        ></div>

                        {/* Milestones Markers */}
                        {timeProgressData.milestones.map((m) => (
                            <div
                                key={m.key}
                                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full z-[60] transition-transform pointer-events-none border group/marker ${m.isToday ? 'bg-white border-blue-500 w-3 h-3 shadow-[0_0_10px_rgba(59,130,246,0.8)]' : 'bg-stone-400 border-stone-300'}`}
                                style={{ left: `${Math.min(99, Math.max(1, m.position))}%` }}
                            >
                                {/* Tooltip / Label - SHOW ALL ON BAR HOVER (group-hover/time) */}
                                {/* ADDED: opacity-0 invisible transition-all duration-200 group-hover/time:delay-500 --> Delays tooltip appearance by 500ms */}
                                <div className={`absolute bottom-full mb-3 opacity-0 invisible group-hover/time:opacity-100 group-hover/time:visible transition-all duration-200 group-hover/time:delay-500 backdrop-blur px-2 py-1 rounded-md border z-[70] shadow-2xl min-w-[80px] text-center left-1/2 -translate-x-1/2 origin-bottom ${m.isToday ? 'bg-stone-800 border-white text-white shadow-[0_0_15px_rgba(255,255,255,0.2)]' : timeProgressData.tooltipStyleClass}`}>
                                    <div className={`font-bold truncate max-w-[150px] mx-auto text-[10px] leading-tight ${m.isToday ? 'text-white' : 'text-stone-100'}`}>{m.label}</div>
                                    <div className="text-stone-300 font-mono text-[9px] leading-tight">{new Date(m.date).toLocaleDateString()}</div>
                                </div>
                            </div>
                        ))}
                    </>
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-stone-600"></div>
                )}
            </div>

            {/* Lower Bar: Content Status (Accessories) */}
            {/* Removed negative margin (-mt-1) to rely on Upper Bar's margin for the gap */}
            <div className="h-[6px] w-full flex rounded-full bg-stone-800 gap-[1px] z-[30]">
                {contentProgressData ? (
                    contentProgressData.map((segment, idx) => {
                        let segAxisClass = 'left-1/2 -translate-x-1/2';
                        if (idx === 0) segAxisClass = 'left-0 translate-x-0';
                        if (idx === contentProgressData.length - 1) segAxisClass = 'right-0 translate-x-0';

                        return (
                            <div
                                key={segment.id}
                                className="h-full relative flex-1 group/segment cursor-help"
                            >
                                {/* INVISIBLE HIT AREA EXPANDER for Segment */}
                                <div className="absolute -top-3 -bottom-3 left-0 right-0 z-[80]" />

                                {/* Visual Background Layer (Handles Opacity) -> No delay on bar brightness for responsiveness */}
                                <div
                                    className={`absolute inset-0 transition-opacity duration-300 opacity-40 group-hover/segment:opacity-100 ${segment.colorClass || ''}`}
                                    style={{ backgroundColor: segment.colorClass ? undefined : segment.color }}
                                ></div>

                                {/* Tooltip */}
                                {/* ADDED: opacity-0 invisible transition-all duration-200 group-hover/segment:delay-500 --> Delays tooltip appearance by 500ms */}
                                <div className={`absolute top-full mt-2 opacity-0 invisible group-hover/segment:opacity-100 group-hover/segment:visible transition-all duration-200 group-hover/segment:delay-500 backdrop-blur px-3 py-2 rounded-md border z-[90] shadow-2xl pointer-events-none ${segAxisClass} min-w-[180px] ${segment.tooltipClass}`}>
                                    <div className="font-bold mb-1">{segment.name}</div>
                                    <div className="text-[10px] font-mono mb-1 opacity-75">{segment.sku}</div>
                                    <div className="text-[10px] mb-2 opacity-90">Status: <span className="font-bold">{segment.status}</span></div>

                                    {segment.dates && segment.dates.length > 0 ? (
                                        <div className="space-y-1 text-left border-t border-white/10 pt-1 mt-1">
                                            {segment.dates.map((d, i) => (
                                                <div key={i} className="flex justify-between text-[10px]">
                                                    <span className="opacity-70">{d.label}:</span>
                                                    <span className="font-mono opacity-100">{new Date(d.date).toLocaleDateString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-[10px] opacity-50 italic border-t border-white/10 pt-1">No charge dates</div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="w-full h-full bg-stone-800/50"></div>
                )}
            </div>
        </div>
    );
};

export default ProjectProgressBar;
